import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Input } from '@rudi/ui/input';
import { useCreateTeam, useTenantTeams, useTenants } from '../api/hooks';
import {
  batchClashes,
  claimedSources,
  networkKey,
  networkSummaries,
  parseNumbers,
  recruitmentError,
  useAddBatch,
  type BatchCode,
  type BatchPractice,
  type CodeKind,
  type RecruitmentBatch,
  type RecruitmentOverview,
} from '../api/recruitment';

const SELECT =
  'h-9 rounded-3xl border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const NEW_TEAM = '__new';

interface PracticeDraft {
  key: number;
  name: string;
  siteId: string | null;
  doctor: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  isCenter: boolean;
  posters: string;
  flyers: string;
}

interface CodeDraft {
  key: number;
  kind: CodeKind;
  value: string;
  purpose: 'campaign' | 'test';
  note: string;
}

let nextKey = 0;

function emptyPractice(): PracticeDraft {
  nextKey += 1;
  return {
    key: nextKey,
    name: '',
    siteId: null,
    doctor: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    isCenter: false,
    posters: '',
    flyers: '',
  };
}

function emptyCode(): CodeDraft {
  nextKey += 1;
  return { key: nextKey, kind: 'source', value: '', purpose: 'campaign', note: '' };
}

function blank(value: string): string | null {
  return value.trim() || null;
}

interface Option {
  id: string;
  title: string;
  detail: string;
}

/** A text field that suggests matching options below it while focused. */
function Suggest({
  label,
  value,
  placeholder,
  options,
  onChange,
  onPick,
}: {
  label: string;
  value: string;
  placeholder: string;
  options: Option[];
  onChange: (value: string) => void;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const query = value.trim().toLowerCase();
  const matches = options
    .filter(
      option =>
        !query ||
        option.title.toLowerCase().includes(query) ||
        option.detail.toLowerCase().includes(query)
    )
    .slice(0, 8);
  return (
    <div className="relative">
      <Input
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onChange={event => {
          onChange(event.target.value);
          setOpen(true);
        }}
        className="h-9"
      />
      {open && matches.length > 0 && (
        <ul
          aria-label={`${label} suggestions`}
          className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border bg-background p-1 shadow-md"
        >
          {matches.map(option => (
            <li key={option.id}>
              <button
                type="button"
                className="w-full rounded-xl px-3 py-1.5 text-left text-sm hover:bg-muted"
                // Keep the field focused until the click lands.
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  onPick(option.id);
                  setOpen(false);
                }}
              >
                <div className="font-medium">{option.title}</div>
                {option.detail && (
                  <div className="text-xs text-muted-foreground">{option.detail}</div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TeamPicker({
  tenantId,
  teamId,
  onTenant,
  onTeam,
}: {
  tenantId: string;
  teamId: string;
  onTenant: (tenantId: string) => void;
  onTeam: (teamId: string, slug: string) => void;
}) {
  const tenants = useTenants();
  const teams = useTenantTeams(tenantId);
  const createTeam = useCreateTeam(tenantId);
  const [creating, setCreating] = useState(false);
  const [displayName, setDisplayName] = useState('');

  const create = () =>
    createTeam.mutate(
      { name: networkKey(displayName), displayName: displayName.trim() },
      {
        onSuccess: team => {
          toast.success(`Team ${team.displayName || team.name} created`);
          setCreating(false);
          setDisplayName('');
          onTeam(team.id, team.name);
        },
        onError: error => toast.error(recruitmentError(error)),
      }
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Organisation"
        className={SELECT}
        value={tenantId}
        onChange={event => {
          onTenant(event.target.value);
          setCreating(false);
        }}
      >
        <option value="">No team (Cortex only)</option>
        {(tenants.data?.tenants ?? []).map(tenant => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
      </select>
      {tenantId && (
        <select
          aria-label="Team"
          className={SELECT}
          value={creating ? NEW_TEAM : teamId}
          onChange={event => {
            if (event.target.value === NEW_TEAM) {
              setCreating(true);
              onTeam('', '');
              return;
            }
            setCreating(false);
            const team = teams.data?.find(item => item.id === event.target.value);
            onTeam(event.target.value, team?.name ?? '');
          }}
        >
          <option value="">Team</option>
          {(teams.data ?? [])
            .filter(team => team.status === 'active')
            .map(team => (
              <option key={team.id} value={team.id}>
                {team.displayName || team.name}
              </option>
            ))}
          <option value={NEW_TEAM}>New team…</option>
        </select>
      )}
      {creating && (
        <>
          <Input
            aria-label="New team name"
            placeholder="e.g. Neurologie UKM"
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            className="h-9 w-56"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!networkKey(displayName) || createTeam.isPending}
            onClick={create}
          >
            Create team
          </Button>
        </>
      )}
    </div>
  );
}

function PracticeRow({
  draft,
  overview,
  onChange,
  onRemove,
}: {
  draft: PracticeDraft;
  overview: RecruitmentOverview;
  onChange: (draft: PracticeDraft) => void;
  onRemove: () => void;
}) {
  const options = useMemo(
    () =>
      overview.sites.map(site => ({
        id: site.id,
        title: site.name,
        detail: [
          site.city,
          site.doctors.join(', '),
          overview.codes
            .filter(code => code.siteId === site.id)
            .map(code => code.label)
            .join(', '),
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [overview]
  );
  const site = draft.siteId ? overview.sites.find(item => item.id === draft.siteId) : undefined;
  const field = (key: keyof PracticeDraft, label: string, className = 'w-40') => (
    <Input
      aria-label={label}
      placeholder={label}
      value={draft[key] as string}
      onChange={event => onChange({ ...draft, [key]: event.target.value })}
      className={`h-9 ${className}`}
    />
  );

  return (
    <li className="rounded-3xl border p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-[16rem] flex-1">
          <Suggest
            label="Practice"
            placeholder="Practice name"
            value={draft.name}
            options={options}
            onChange={name => onChange({ ...draft, name, siteId: null })}
            onPick={id =>
              onChange({
                ...draft,
                siteId: id,
                name: overview.sites.find(item => item.id === id)?.name ?? draft.name,
              })
            }
          />
        </div>
        {field('posters', 'Poster numbers')}
        {field('flyers', 'Flyer numbers')}
        <Button variant="ghost" size="sm" aria-label="Remove practice" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {site ? (
        <p className="mt-2 text-xs text-muted-foreground">
          <Badge variant="secondary" className="mr-2">
            Known practice
          </Badge>
          {[site.city, site.network].filter(Boolean).join(' · ')}
        </p>
      ) : (
        draft.name.trim() && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline">New practice</Badge>
            {field('doctor', 'Doctor', 'w-48')}
            {field('street', 'Street', 'w-48')}
            {field('houseNumber', 'No.', 'w-20')}
            {field('postalCode', 'Postal code', 'w-28')}
            {field('city', 'City', 'w-36')}
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={draft.isCenter}
                onChange={event => onChange({ ...draft, isCenter: event.target.checked })}
              />
              Study center itself
            </label>
          </div>
        )
      )}
    </li>
  );
}

function CodeRow({
  draft,
  onChange,
  onRemove,
}: {
  draft: CodeDraft;
  onChange: (draft: CodeDraft) => void;
  onRemove: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Code kind"
        className={SELECT}
        value={draft.kind}
        onChange={event => onChange({ ...draft, kind: event.target.value as CodeKind })}
      >
        <option value="source">Source value</option>
        <option value="poster">Poster numbers</option>
        <option value="flyer">Flyer numbers</option>
      </select>
      <Input
        aria-label="Code"
        placeholder={draft.kind === 'source' ? 'e.g. g3' : 'e.g. 1234'}
        value={draft.value}
        onChange={event => onChange({ ...draft, value: event.target.value })}
        className="h-9 w-40"
      />
      <select
        aria-label="Purpose"
        className={SELECT}
        value={draft.purpose}
        onChange={event =>
          onChange({ ...draft, purpose: event.target.value as CodeDraft['purpose'] })
        }
      >
        <option value="campaign">Campaign</option>
        <option value="test">Test</option>
      </select>
      <Input
        aria-label="Code note"
        placeholder="Note, e.g. Google ads October"
        value={draft.note}
        onChange={event => onChange({ ...draft, note: event.target.value })}
        className="h-9 min-w-[12rem] flex-1"
      />
      <Button variant="ghost" size="sm" aria-label="Remove code" onClick={onRemove}>
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

/** The batch the drafts describe, and what keeps it from being sent. */
function buildBatch(
  overview: RecruitmentOverview,
  network: string,
  tenantId: string,
  teamId: string,
  note: string,
  practiceDrafts: PracticeDraft[],
  codeDrafts: CodeDraft[]
): { batch: RecruitmentBatch; problems: string[]; empty: boolean } {
  const problems: string[] = [];
  const practices: BatchPractice[] = [];
  for (const draft of practiceDrafts) {
    const name = draft.name.trim();
    if (!name && !draft.posters.trim() && !draft.flyers.trim()) continue;
    const posters = parseNumbers(draft.posters);
    const flyers = parseNumbers(draft.flyers);
    const label = name || 'A practice';
    if (posters.error) problems.push(`${label}: posters ${posters.error}`);
    if (flyers.error) problems.push(`${label}: flyers ${flyers.error}`);
    if (!name) problems.push('Each practice needs a name.');
    else if (!posters.ranges.length && !flyers.ranges.length && !posters.error && !flyers.error)
      problems.push(`${label}: add poster or flyer numbers.`);
    practices.push({
      ...(draft.siteId
        ? { siteId: draft.siteId }
        : {
            new: {
              name,
              doctors: draft.doctor.trim() ? [draft.doctor.trim()] : [],
              street: blank(draft.street),
              houseNumber: blank(draft.houseNumber),
              postalCode: blank(draft.postalCode),
              city: blank(draft.city),
              isCenter: draft.isCenter,
            },
          }),
      posters: posters.ranges,
      flyers: flyers.ranges,
    });
  }
  const codes: BatchCode[] = [];
  for (const draft of codeDrafts) {
    const value = draft.value.trim();
    if (!value) continue;
    const common = { purpose: draft.purpose, note: blank(draft.note) };
    if (draft.kind === 'source') {
      codes.push({ kind: 'source', source: value, ...common });
      continue;
    }
    const parsed = parseNumbers(value);
    if (parsed.error) problems.push(`Code ${value}: ${parsed.error}`);
    for (const range of parsed.ranges) {
      codes.push({ kind: draft.kind, first: range.first, last: range.last, ...common });
    }
  }
  if (practices.length && !network.trim()) problems.push('Name the network of these practices.');
  if (tenantId && !teamId) problems.push('Pick a team, or no organisation.');
  const batch: RecruitmentBatch = {
    network: blank(network),
    tenantId: tenantId && teamId ? tenantId : null,
    teamId: tenantId && teamId ? teamId : null,
    note: blank(note),
    practices,
    codes,
  };
  return {
    batch,
    problems: [...problems, ...batchClashes(overview, batch)],
    empty: !practices.length && !codes.length,
  };
}

export function QrBatchDialog({
  overview,
  onClose,
}: {
  overview: RecruitmentOverview;
  onClose: () => void;
}) {
  const [network, setNetwork] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [note, setNote] = useState(`Mailing ${new Date().toISOString().slice(0, 10)}`);
  const [practices, setPractices] = useState<PracticeDraft[]>(() => [emptyPractice()]);
  const [codes, setCodes] = useState<CodeDraft[]>([]);
  const add = useAddBatch();

  const networks = useMemo(
    () => networkSummaries(overview).filter(summary => summary.network !== null),
    [overview]
  );
  const { batch, problems, empty } = buildBatch(
    overview,
    network,
    tenantId,
    teamId,
    note,
    practices,
    codes
  );
  const claimed = claimedSources(overview, batch);

  const pickNetwork = (key: string) => {
    setNetwork(key);
    const team = networks.find(summary => summary.network === key)?.teams[0];
    if (team) {
      setTenantId(team.tenantId);
      setTeamId(team.teamId);
    }
  };

  const submit = () =>
    add.mutate(batch, {
      onSuccess: result => {
        toast.success(
          `Added ${result.codesCreated} codes and ${result.sitesCreated} new practices; ${result.practicesCreated} team practices and ${result.linksCreated} links created`
        );
        onClose();
      },
      onError: error => toast.error(recruitmentError(error)),
    });

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl">
        <DialogHeader>
          <DialogTitle>Add batch</DialogTitle>
          <DialogDescription>
            One mailing of posters and flyers. Practices that belong to the chosen team appear in
            its practice list; nobody gets an email.
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Network</h3>
          <div className="flex flex-wrap items-start gap-2">
            <div className="w-64">
              <Suggest
                label="Network"
                placeholder="Search networks, or type a new one"
                value={network}
                options={networks.map(summary => ({
                  id: summary.network ?? '',
                  title: summary.network ?? '',
                  detail: summary.teams.length
                    ? summary.teams.map(team => `${team.tenantName} · ${team.teamName}`).join(', ')
                    : 'No team yet',
                }))}
                onChange={setNetwork}
                onPick={pickNetwork}
              />
            </div>
            <TeamPicker
              tenantId={tenantId}
              teamId={teamId}
              onTenant={id => {
                setTenantId(id);
                setTeamId('');
              }}
              onTeam={(id, slug) => {
                setTeamId(id);
                if (slug && !network.trim()) setNetwork(slug);
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Create a new organisation under Tenants first. Without a team the practices show in
            Cortex only.
          </p>
          <Input
            aria-label="Mailing note"
            value={note}
            onChange={event => setNote(event.target.value)}
            className="h-9 w-80"
          />
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Practices</h3>
          <p className="text-xs text-muted-foreground">
            Numbers as in the QR codes: <code>41-44, 47</code>. Posters open{' '}
            <code>?source=location_N</code>, flyers <code>?source=flyer_N</code>.
          </p>
          <ul className="space-y-2">
            {practices.map(draft => (
              <PracticeRow
                key={draft.key}
                draft={draft}
                overview={overview}
                onChange={next =>
                  setPractices(list => list.map(item => (item.key === next.key ? next : item)))
                }
                onRemove={() => setPractices(list => list.filter(item => item.key !== draft.key))}
              />
            ))}
          </ul>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPractices(list => [...list, emptyPractice()])}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add practice
          </Button>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Campaign and test codes</h3>
          {codes.length > 0 && (
            <ul className="space-y-2">
              {codes.map(draft => (
                <CodeRow
                  key={draft.key}
                  draft={draft}
                  onChange={next =>
                    setCodes(list => list.map(item => (item.key === next.key ? next : item)))
                  }
                  onRemove={() => setCodes(list => list.filter(item => item.key !== draft.key))}
                />
              ))}
            </ul>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCodes(list => [...list, emptyCode()])}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add campaign or test code
          </Button>
        </section>

        {claimed.length > 0 && (
          <p role="status" className="text-xs text-muted-foreground">
            Chats already recorded will count for this batch:{' '}
            {claimed
              .map(source => `${source.source} (${source.totals.chatsStarted} chats)`)
              .join(', ')}
            .
          </p>
        )}
        {problems.length > 0 && (
          <ul aria-label="Problems" className="list-disc pl-5 text-xs text-destructive">
            {problems.map((problem, index) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={empty || problems.length > 0 || add.isPending} onClick={submit}>
            Add batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
